import { getAttributionStorageKey, TRACKED_QUERY_KEYS } from './leadAttribution';

interface Turnstile {
  render(container: string | HTMLElement, opts: TurnstileRenderOptions): string;
  getResponse(widgetId?: string): string;
  reset(widgetId?: string): void;
  execute(widgetId?: string): void;
  remove(widgetId?: string): void;
  ready(cb: () => void): void;
}

interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'flexible' | 'compact';
  // execution: 'execute' 延迟挑战到显式 execute() 才跑（避免 render 时自动执行 → 与提交时
  // 的 execute() 冲突触发 300010）。appearance: 'execute' 仅控制可见性（交互前隐藏）。
  execution?: 'render' | 'execute';
  appearance?: 'always' | 'execute' | 'interaction-only';
}

type LeadFormElement = HTMLFormElement & {
  _submitHandler?: (event: Event) => void;
  _onSuccess?: () => void;
  _turnstileWidgetId?: string;
};

type LeadFormResponse = {
  success: boolean;
  message?: string;
  downloadUrl?: string;
  eventName?: string;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
    onTurnstileLoad?: () => void;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function ensureDataLayer() {
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }

  return window.dataLayer;
}

function pushEvent(event: string, payload: Record<string, unknown> = {}) {
  ensureDataLayer().push({
    event,
    ...payload,
  });
}

function readStoredAttribution() {
  try {
    const raw = window.localStorage.getItem(getAttributionStorageKey());
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeStoredAttribution(payload: Record<string, string>) {
  try {
    window.localStorage.setItem(getAttributionStorageKey(), JSON.stringify(payload));
  } catch {
    return;
  }
}

function captureAttributionFromLocation() {
  const url = new URL(window.location.href);
  const current = readStoredAttribution();
  const next = { ...current };
  let changed = false;

  for (const key of TRACKED_QUERY_KEYS) {
    const value = url.searchParams.get(key);
    if (value) {
      next[key] = value;
      changed = true;
    }
  }

  if (!next.first_landing_page) {
    next.first_landing_page = `${url.pathname}${url.search}${url.hash}`;
    changed = true;
  }

  if (document.referrer && !next.referrer) {
    next.referrer = document.referrer;
    changed = true;
  }

  if (changed) {
    writeStoredAttribution(next);
  }

  return next;
}

function setHiddenField(form: HTMLFormElement, name: string, value: string) {
  let input = form.querySelector(`input[name="${name}"]`) as HTMLInputElement | null;
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }

  input.value = value;
}

function hydrateLeadContext(form: HTMLFormElement) {
  const attribution = captureAttributionFromLocation();
  const pageUrl = window.location.href;
  const currentPage = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  setHiddenField(form, 'pageUrl', pageUrl);
  setHiddenField(form, 'pageTitle', document.title);
  setHiddenField(form, 'current_page', currentPage);
  setHiddenField(form, 'current_page_title', document.title);

  if (document.referrer) {
    setHiddenField(form, 'referrer', attribution.referrer || document.referrer);
  }

  Object.entries(attribution).forEach(([key, value]) => {
    if (value) {
      setHiddenField(form, key, value);
    }
  });
}




// ── Cloudflare Turnstile（本站唯一真人检测）─────────────────────────────────
// Turnstile 脚本从 challenges.cloudflare.com 加载，在 Cloudflare 站点上稳定可达，
// 无 reCAPTCHA 的 google.com 脚本加载失败问题。

export function loadTurnstile(): void {
  if (!document.querySelector('script#turnstile-script')) {
    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (typeof window.onTurnstileLoad === 'function') {
        window.onTurnstileLoad();
      }
    };
    document.head.appendChild(script);
  }
}

function waitForTurnstile(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      if (window.turnstile) {
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve();
        return;
      }
      setTimeout(check, 50);
    };
    check();
  });
}

type TurnstileContainer = HTMLElement & {
  _turnstileWidgetId?: string;
};

export async function getTurnstileTokenForAction(
  siteKey: string,
  container?: HTMLElement | null
): Promise<string> {
  if (!siteKey) {
    throw new Error('Turnstile is not properly configured (missing site key)');
  }

  if (!window.turnstile) {
    loadTurnstile();
    await waitForTurnstile(5000);
  }

  if (!window.turnstile) {
    throw new Error('Turnstile is not properly configured (script not loaded)');
  }

  const el = (container || null) as TurnstileContainer | null;

  // 每个容器只 render 一次 widget（缓存在 el._turnstileWidgetId）。
  let widgetId = el?._turnstileWidgetId;
  let tokenResolver: ((token: string) => void) | null = null;
  let tokenRejecter: ((err: Error) => void) | null = null;

  if (!widgetId) {
    // 自动执行模式（与已验证可用的 fbccd692 部署一致）：
    // 仅用 appearance:'execute'（控制可见性，交互前隐藏 widget），**不设 execution:'execute'**，
    // 故 widget 在 render 后**自动跑挑战**，成功后经 callback 把 token 交给 tokenResolver。
    // 这是昨天真人检测工作完美的版本；不要改回 execution:'execute'（那会改成交互时才跑，
    // 与下方冗余的 execute() 撞车触发 300010）。
    widgetId = window.turnstile.render(el || 'body', {
      sitekey: siteKey,
      appearance: 'execute',
      callback: (token: string) => {
        if (token && tokenResolver) {
          tokenResolver(token);
        }
      },
    });
    if (el) {
      el._turnstileWidgetId = widgetId;
    }
  }

  // 容器里可能已有通过验证的 token（widget 自动执行已成功）
  const existing = window.turnstile.getResponse(widgetId);
  if (existing) {
    return existing;
  }

  // 经 callback 等待 token：首次挑战（冷启动）可能慢于 5s，故放宽到 15s 安全超时。
  const tokenPromise = new Promise<string>((resolve, reject) => {
    tokenResolver = resolve;
    tokenRejecter = reject;
    setTimeout(() => {
      if (tokenRejecter) {
        tokenRejecter(new Error('Failed to verify you are human. Please try again.'));
      }
    }, 15000);
  });

  // 兜底：若 widget 因某些边界未自动执行（极少），显式补一次 execute()。
  // 此时 token 通常已由自动执行的 callback 拿到（getResponse 非空 → 上面已 return）；
  // 若仍为空，则 execute() 触发挑战，callback 仍会照常回 token。
  if (!window.turnstile.getResponse(widgetId)) {
    try {
      window.turnstile.execute(widgetId);
    } catch {
      /* 若已自动执行中，忽略——callback 会照常返回 token */
    }
  }

  try {
    return await tokenPromise;
  } catch (err) {
    // 超时/失败：reset 清场，便于用户重试拿到新 token。
    try {
      if (widgetId) window.turnstile.reset(widgetId);
    } catch {
      /* ignore */
    }
    throw err;
  } finally {
    tokenResolver = null;
    tokenRejecter = null;
  }
}

export function removeTurnstile(container?: HTMLElement | null): void {
  const el = container as TurnstileContainer | null;
  const widgetId = el?._turnstileWidgetId;
  if (window.turnstile && widgetId) {
    try {
      window.turnstile.remove(widgetId);
    } catch {
      /* ignore */
    }
    el._turnstileWidgetId = undefined;
  }
}

export function resetTurnstile(container?: HTMLElement | null): void {
  const el = container as TurnstileContainer | null;
  const widgetId = el?._turnstileWidgetId;
  if (window.turnstile && widgetId) {
    window.turnstile.reset(widgetId);
  }
}

// 提交成功后隐藏真人检测容器，给出干净的「已提交」外观；
// 下次提交前由 handleFormSubmit 重新显示并触发新一次验证。
function hideTurnstileContainer(container?: HTMLElement | null): void {
  if (container) {
    container.style.display = 'none';
  }
}

// 提交前显示真人检测容器（覆盖上次成功后被隐藏的情况，使重新提交可验证）。
function showTurnstileContainer(container?: HTMLElement | null): void {
  if (container) {
    container.style.display = '';
  }
}

async function submitLeadForm(form: LeadFormElement, formData: FormData) {
  const endpoint = form.getAttribute('data-api-endpoint') || '/api/contactform';
  const response = await fetch(endpoint, {
    method: 'POST',
    body: formData,
  });

  const result = (await response.json()) as LeadFormResponse;

  if (!response.ok || !result.success) {
    throw new Error(result.message || 'Failed to submit form');
  }

  return result;
}

export async function handleFormSubmit(
  form: LeadFormElement,
  siteKey: string,
  callbacks: {
    onSuccess: (message: string) => void;
    onError: (message: string) => void;
    onSubmitStart: () => void;
    onSubmitEnd: () => void;
  }
) {
  const turnstileContainer = form.querySelector<HTMLElement>('[data-turnstile-container]');
  try {
    callbacks.onSubmitStart();
    hydrateLeadContext(form);

    // 真人检测：默认且唯一使用 Cloudflare Turnstile（不再 fallback 到 Google reCAPTCHA）。
    // Turnstile 是本站首选方案——Cloudflare 自家、挑战更轻、与站点同域、无需 Google 脚本。
    // 若 Turnstile 不可用（site key 缺失 / 网络拦截 / widget 渲染失败），直接报错提示，
    // 不再静默降级到 reCAPTCHA，避免两套密钥体系错配（曾导致 400020）与防护降级。
    let token = '';
    const tokenField = 'turnstileToken';
    try {
      token = await getTurnstileTokenForAction(siteKey, turnstileContainer);
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to verify you are human. Please try again.');
    }
    const formData = new FormData(form);
    formData.set(tokenField, token);

    const result = await submitLeadForm(form, formData);
    callbacks.onSuccess(result.message || 'Form submitted successfully.');

    const eventName =
      result.eventName ||
      form.getAttribute('data-success-event') ||
      `${form.getAttribute('data-form-kind') || 'lead'}_submit_success`;

    pushEvent(eventName, {
      formKind: form.getAttribute('data-form-kind') || 'lead',
      source: form.getAttribute('data-source') || '',
      pageUrl: window.location.href,
    });

    if (result.downloadUrl && form.getAttribute('data-auto-download') !== 'false') {
      window.open(result.downloadUrl, '_blank', 'noopener');
      pushEvent('download_asset_unlocked', {
        formKind: form.getAttribute('data-form-kind') || 'download',
        downloadUrl: result.downloadUrl,
      });
    }

    form.reset();
    hydrateLeadContext(form);
    // 提交成功后隐藏真人检测区并 reset，避免 widget 卡在「等待对钩」的残留状态。
    // 下次提交前由 showTurnstileContainer 重新显示并触发新挑战。
    hideTurnstileContainer(turnstileContainer);
    resetTurnstile(turnstileContainer);

    if (typeof form._onSuccess === 'function') {
      form._onSuccess();
    }
  } catch (error) {
    callbacks.onError(error instanceof Error ? error.message : 'Failed to submit form');
    // 校验失败（token 过期/被消费）时重置，便于用户重试拿到新 token
    resetTurnstile(turnstileContainer);
  } finally {
    callbacks.onSubmitEnd();
  }
}

export function initializeLeadForm(form: LeadFormElement) {
  if (form.hasAttribute('data-initialized')) {
    hydrateLeadContext(form);
    return;
  }

  form.setAttribute('data-initialized', 'true');
  // 仅用 Turnstile site key（不再回退 reCAPTCHA）。缺失则表单无法做真人检测。
  const siteKey = form.getAttribute('data-turnstile-site-key') || '';

  // Turnstile（本站唯一真人检测）：首次用户与表单交互时才加载脚本，避免首页主线程阻塞。
  // 交互到点击提交通常有 1-3s，足够脚本异步加载完毕。
  if (siteKey) {
    let turnstileLoaded = false;
    const lazyLoadTurnstile = () => {
      if (turnstileLoaded) return;
      turnstileLoaded = true;
      loadTurnstile();
      interactionTriggers.forEach((trigger) =>
        form.removeEventListener(trigger.event, trigger.handler, trigger.options)
      );
    };
    const interactionTriggers: Array<{
      event: string;
      handler: () => void;
      options: AddEventListenerOptions;
    }> = [
      { event: 'focusin', handler: lazyLoadTurnstile, options: { passive: true } },
      { event: 'pointerdown', handler: lazyLoadTurnstile, options: { passive: true } },
      { event: 'keydown', handler: lazyLoadTurnstile, options: {} },
    ];
    interactionTriggers.forEach((trigger) =>
      form.addEventListener(trigger.event, trigger.handler, trigger.options)
    );
  }

  const formStatus = form.querySelector('[data-form-status]') as HTMLElement | null;
  const successMessage = form.querySelector('[data-form-success]') as HTMLElement | null;
  const errorMessage = form.querySelector('[data-form-error]') as HTMLElement | null;
  const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
  const loadingLabel =
    form.getAttribute('data-loading-label') || submitButton?.textContent || 'Sending...';
  const defaultLabel = submitButton?.textContent || 'Send';

  if (!formStatus || !successMessage || !errorMessage || !submitButton) {
    return;
  }

  const hideMessages = () => {
    formStatus.classList.add('hidden');
    successMessage.classList.add('hidden');
    errorMessage.classList.add('hidden');
  };

  const showSuccess = (message: string) => {
    successMessage.textContent = message;
    successMessage.classList.remove('hidden');
    formStatus.classList.remove('hidden');
    formStatus.focus();
  };

  const showError = (message: string) => {
    errorMessage.textContent = message;
    errorMessage.classList.remove('hidden');
    formStatus.classList.remove('hidden');
    formStatus.focus();
  };

  const oldHandler = form._submitHandler;
  if (oldHandler) {
    form.removeEventListener('submit', oldHandler);
  }

  hydrateLeadContext(form);

  // U-1: 字段级内联校验。无效字段加 aria-invalid + 错误边框样式，
  // 输入时清除标记。提交时调 reportValidity 触发原生气泡并把焦点移到首个无效字段。
  const invalidFieldClass = 'lead-field-invalid';
  const clearFieldError = (field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => {
    field.removeAttribute('aria-invalid');
    field.classList.remove(invalidFieldClass);
  };
  const markFieldError = (field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => {
    if (!field.validity.valid) {
      field.setAttribute('aria-invalid', 'true');
      field.classList.add(invalidFieldClass);
    } else {
      clearFieldError(field);
    }
  };
  const validateAndMarkFields = (formEl: LeadFormElement): boolean => {
    const fields = formEl.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select'
    );
    let firstInvalid: (HTMLElement & { focus: () => void }) | null = null;
    fields.forEach((field) => {
      markFieldError(field);
      if (!field.validity.valid && !firstInvalid) {
        firstInvalid = field;
      }
    });
    if (firstInvalid) {
      // reportValidity 触发浏览器原生字段级气泡并聚焦首个无效字段
      (formEl as HTMLFormElement).reportValidity();
      (firstInvalid as HTMLInputElement).focus();
      return false;
    }
    return true;
  };
  // 输入时清除该字段的错误标记
  form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
    'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select'
  ).forEach(field => {
    field.addEventListener('input', () => clearFieldError(field));
    field.addEventListener('change', () => clearFieldError(field));
  });

  const handleSubmit = async (event: Event) => {
    event.preventDefault();
    hideMessages();

    if (!siteKey) {
      showError('Form configuration error');
      return;
    }

    // 重新提交前确保真人检测区可见（上次成功后被 hideTurnstileContainer 隐藏）。
    showTurnstileContainer(form.querySelector<HTMLElement>('[data-turnstile-container]'));

    // U-1: 字段级内联校验。提交前标记无效字段（aria-invalid + 红框），
    // 并触发浏览器原生 reportValidity 把焦点移到第一个无效字段。
    if (!validateAndMarkFields(form)) {
      return;
    }

    await handleFormSubmit(form, siteKey, {
      onSuccess: showSuccess,
      onError: showError,
      onSubmitStart: () => {
        submitButton.disabled = true;
        submitButton.textContent = loadingLabel;
        form.setAttribute('aria-busy', 'true');
      },
      onSubmitEnd: () => {
        submitButton.disabled = false;
        submitButton.textContent = defaultLabel;
        form.removeAttribute('aria-busy');
      },
    });
  };

  form._submitHandler = handleSubmit;
  form.addEventListener('submit', handleSubmit);
}

export function initializeAllLeadForms() {
  captureAttributionFromLocation();
  const forms = document.querySelectorAll<LeadFormElement>('form[data-lead-form]:not([data-initialized])');
  forms.forEach((form) => initializeLeadForm(form));
}

export function cleanupLeadForms() {
  const forms = document.querySelectorAll<LeadFormElement>('form[data-lead-form]');
  forms.forEach((form) => {
    const oldHandler = form._submitHandler;
    if (oldHandler) {
      form.removeEventListener('submit', oldHandler);
      delete form._submitHandler;
    }
    // astro:page-load 会重建 DOM（View Transitions 后旧 body 节点被换掉），
    // 旧 widget 的 iframe 已随节点销毁，但其 turnstile widgetId 仍登记在 Turnstile 内部；
    // remove 之，避免挑战 iframe 的 postMessage 指向已被置换的 window 起源（"target origin
    // does not match recipient window" 的根因之一），也让下次渲染从干净状态开始。
    removeTurnstile(form.querySelector<HTMLElement>('[data-turnstile-container]'));
  });
}
